import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  Radio,
  message,
  Tabs,
  Typography,
  Row,
  Col,
  Statistic,
  DatePicker,
  Popconfirm,
  List as AntList,
  Avatar,
} from 'antd';
import {
  SendOutlined,
  BellOutlined,
  UserOutlined,
  CarOutlined,
  GlobalOutlined,
  HistoryOutlined,
  FileTextOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  TeamOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useCustom, useApiUrl } from '@refinedev/core';
import { apiClient } from '../../lib/api-client';

const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Text } = Typography;

interface NotificationHistory {
  id: number;
  title: string;
  body: string;
  targetType: 'USER' | 'ALL_USERS' | 'ALL_DRIVERS' | 'REGION';
  targetId?: number;
  targetName?: string;
  sentAt: string;
  sentBy: string;
  recipientCount: number;
  deliveredCount: number;
  readCount: number;
}

interface NotificationTemplate {
  id: number;
  name: string;
  title: string;
  body: string;
  category: string;
  createdAt: string;
}

interface NotificationStats {
  totalSent: number;
  totalDelivered: number;
  totalRead: number;
  todaySent: number;
}

export const NotificationList = () => {
  const apiUrl = useApiUrl();
  const location = useLocation();
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<NotificationTemplate | null>(null);
  const [sendForm] = Form.useForm();
  const [templateForm] = Form.useForm();
  const [sending, setSending] = useState(false);
  const [targetType, setTargetType] = useState<string>('ALL_USERS');
  const [activeTab, setActiveTab] = useState('history');

  // Detect if we're on bus section
  const isBusSection = location.pathname.startsWith('/bus/');
  const serviceType = isBusSection ? 'bus' : 'taxi';

  // Fetch notification history
  const { data: historyData, isLoading: historyLoading, refetch: refetchHistory } = useCustom<{
    data: NotificationHistory[];
    total: number;
  }>({
    url: `${apiUrl}/admin/notifications`,
    method: 'get',
    config: {
      query: { serviceType },
    },
  });

  // Fetch templates
  const { data: templatesData, isLoading: templatesLoading, refetch: refetchTemplates } = useCustom<{
    data: NotificationTemplate[];
  }>({
    url: `${apiUrl}/admin/notifications/templates`,
    method: 'get',
    config: {
      query: { serviceType },
    },
  });

  // Fetch stats
  const { data: statsData } = useCustom<NotificationStats>({
    url: `${apiUrl}/admin/notifications/stats`,
    method: 'get',
    config: {
      query: { serviceType },
    },
  });

  // Fetch regions for dropdown
  const { data: regionsData } = useCustom<{ data: { id: number; name: string }[] }>({
    url: `${apiUrl}/admin/regions`,
    method: 'get',
  });

  const stats = statsData?.data;
  const history = historyData?.data?.data || [];
  const templates = templatesData?.data?.data || [];
  const regions = regionsData?.data?.data || [];

  const handleSendNotification = async (values: any) => {
    setSending(true);
    try {
      await apiClient.post('/admin/notifications/send', {
        title: values.title,
        body: values.body,
        targetType: values.targetType,
        targetId: values.targetId,
      });
      message.success('Bildirishnoma muvaffaqiyatli yuborildi');
      setSendModalOpen(false);
      sendForm.resetFields();
      refetchHistory();
    } catch (error) {
      message.error('Bildirishnoma yuborishda xatolik');
    } finally {
      setSending(false);
    }
  };

  const handleSaveTemplate = async (values: any) => {
    try {
      if (selectedTemplate) {
        await apiClient.put(`/admin/notifications/templates/${selectedTemplate.id}`, values);
        message.success('Shablon yangilandi');
      } else {
        await apiClient.post('/admin/notifications/templates', values);
        message.success('Shablon yaratildi');
      }
      setTemplateModalOpen(false);
      templateForm.resetFields();
      setSelectedTemplate(null);
      refetchTemplates();
    } catch (error) {
      message.error('Xatolik yuz berdi');
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    try {
      await apiClient.delete(`/admin/notifications/templates/${id}`);
      message.success("Shablon o'chirildi");
      refetchTemplates();
    } catch (error) {
      message.error("Shablonni o'chirishda xatolik");
    }
  };

  const handleUseTemplate = (template: NotificationTemplate) => {
    sendForm.setFieldsValue({
      title: template.title,
      body: template.body,
    });
    setSendModalOpen(true);
  };

  const historyColumns: ColumnsType<NotificationHistory> = [
    {
      title: 'Sarlavha',
      dataIndex: 'title',
      key: 'title',
      render: (title, record) => (
        <div>
          <Text strong>{title}</Text>
          <br />
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.body.length > 50 ? record.body.slice(0, 50) + '...' : record.body}
          </Text>
        </div>
      ),
    },
    {
      title: 'Manzil',
      dataIndex: 'targetType',
      key: 'targetType',
      render: (type, record) => {
        const config: Record<string, { color: string; icon: React.ReactNode; label: string }> = {
          USER: { color: 'blue', icon: <UserOutlined />, label: record.targetName || 'Foydalanuvchi' },
          ALL_USERS: { color: 'green', icon: <TeamOutlined />, label: 'Barcha foydalanuvchilar' },
          ALL_DRIVERS: { color: 'orange', icon: <CarOutlined />, label: 'Barcha haydovchilar' },
          REGION: { color: 'purple', icon: <GlobalOutlined />, label: record.targetName || 'Viloyat' },
        };
        const c = config[type] || { color: 'default', icon: <BellOutlined />, label: type };
        return (
          <Tag color={c.color} icon={c.icon}>
            {c.label}
          </Tag>
        );
      },
    },
    {
      title: 'Yuborilgan',
      dataIndex: 'sentAt',
      key: 'sentAt',
      render: (date) => dayjs(date).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Qabul qiluvchilar',
      key: 'recipients',
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.recipientCount} ta yuborildi</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.deliveredCount} yetkazildi, {record.readCount} o'qildi
          </Text>
        </Space>
      ),
    },
    {
      title: 'Yuboruvchi',
      dataIndex: 'sentBy',
      key: 'sentBy',
    },
  ];

  const templateColumns: ColumnsType<NotificationTemplate> = [
    {
      title: 'Nomi',
      dataIndex: 'name',
      key: 'name',
      render: (name) => <Text strong>{name}</Text>,
    },
    {
      title: 'Sarlavha',
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: 'Kategoriya',
      dataIndex: 'category',
      key: 'category',
      render: (cat) => <Tag>{cat}</Tag>,
    },
    {
      title: 'Yaratilgan',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => dayjs(date).format('DD.MM.YYYY'),
    },
    {
      title: 'Amallar',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SendOutlined />}
            onClick={() => handleUseTemplate(record)}
          >
            Yuborish
          </Button>
          <Button
            size="small"
            icon={<EditOutlined />}
            onClick={() => {
              setSelectedTemplate(record);
              templateForm.setFieldsValue(record);
              setTemplateModalOpen(true);
            }}
          />
          <Popconfirm
            title="Shablonni o'chirishni xohlaysizmi?"
            onConfirm={() => handleDeleteTemplate(record.id)}
            okText="Ha"
            cancelText="Yo'q"
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* Stats */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title="Jami yuborilgan"
              value={stats?.totalSent || 0}
              prefix={<SendOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Yetkazilgan"
              value={stats?.totalDelivered || 0}
              prefix={<BellOutlined />}
              valueStyle={{ color: '#27AE60' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="O'qilgan"
              value={stats?.totalRead || 0}
              prefix={<FileTextOutlined />}
              valueStyle={{ color: '#3498DB' }}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="Bugun yuborilgan"
              value={stats?.todaySent || 0}
              prefix={<HistoryOutlined />}
              valueStyle={{ color: '#9B59B6' }}
            />
          </Card>
        </Col>
      </Row>

      {/* Main Content */}
      <Card
        title={
          <Space>
            <BellOutlined />
            <span>Bildirishnomalar</span>
          </Space>
        }
        extra={
          <Button
            type="primary"
            icon={<SendOutlined />}
            onClick={() => setSendModalOpen(true)}
          >
            Bildirishnoma yuborish
          </Button>
        }
      >
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={[
            {
              key: 'history',
              label: (
                <span>
                  <HistoryOutlined />
                  Tarix
                </span>
              ),
              children: (
                <Table
                  columns={historyColumns}
                  dataSource={history}
                  rowKey="id"
                  loading={historyLoading}
                  pagination={{ pageSize: 10 }}
                />
              ),
            },
            {
              key: 'templates',
              label: (
                <span>
                  <FileTextOutlined />
                  Shablonlar
                </span>
              ),
              children: (
                <div>
                  <div style={{ marginBottom: 16 }}>
                    <Button
                      type="primary"
                      icon={<PlusOutlined />}
                      onClick={() => {
                        setSelectedTemplate(null);
                        templateForm.resetFields();
                        setTemplateModalOpen(true);
                      }}
                    >
                      Yangi shablon
                    </Button>
                  </div>
                  <Table
                    columns={templateColumns}
                    dataSource={templates}
                    rowKey="id"
                    loading={templatesLoading}
                    pagination={{ pageSize: 10 }}
                  />
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Send Notification Modal */}
      <Modal
        title="Bildirishnoma yuborish"
        open={sendModalOpen}
        onCancel={() => {
          setSendModalOpen(false);
          sendForm.resetFields();
        }}
        footer={null}
        width={600}
      >
        <Form
          form={sendForm}
          layout="vertical"
          onFinish={handleSendNotification}
          initialValues={{ targetType: 'ALL_USERS' }}
        >
          <Form.Item
            name="targetType"
            label="Kimga yuborish"
            rules={[{ required: true, message: 'Manzilni tanlang' }]}
          >
            <Radio.Group onChange={(e) => setTargetType(e.target.value)}>
              <Space direction="vertical">
                <Radio value="ALL_USERS">
                  <Space>
                    <TeamOutlined />
                    Barcha foydalanuvchilarga
                  </Space>
                </Radio>
                <Radio value="ALL_DRIVERS">
                  <Space>
                    <CarOutlined />
                    Barcha haydovchilarga
                  </Space>
                </Radio>
                <Radio value="REGION">
                  <Space>
                    <GlobalOutlined />
                    Viloyat bo'yicha
                  </Space>
                </Radio>
                <Radio value="USER">
                  <Space>
                    <UserOutlined />
                    Muayyan foydalanuvchiga
                  </Space>
                </Radio>
              </Space>
            </Radio.Group>
          </Form.Item>

          {targetType === 'REGION' && (
            <Form.Item
              name="targetId"
              label="Viloyat"
              rules={[{ required: true, message: 'Viloyatni tanlang' }]}
            >
              <Select
                placeholder="Viloyatni tanlang"
                options={regions.map((r) => ({ value: r.id, label: r.name }))}
              />
            </Form.Item>
          )}

          {targetType === 'USER' && (
            <Form.Item
              name="targetId"
              label="Foydalanuvchi ID"
              rules={[{ required: true, message: 'Foydalanuvchi ID kiriting' }]}
            >
              <Input placeholder="Foydalanuvchi ID" type="number" />
            </Form.Item>
          )}

          <Form.Item
            name="title"
            label="Sarlavha"
            rules={[{ required: true, message: 'Sarlavha kiriting' }]}
          >
            <Input placeholder="Bildirishnoma sarlavhasi" maxLength={100} />
          </Form.Item>

          <Form.Item
            name="body"
            label="Matn"
            rules={[{ required: true, message: 'Matn kiriting' }]}
          >
            <TextArea
              placeholder="Bildirishnoma matni"
              rows={4}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setSendModalOpen(false)}>Bekor qilish</Button>
              <Button type="primary" htmlType="submit" loading={sending} icon={<SendOutlined />}>
                Yuborish
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* Template Modal */}
      <Modal
        title={selectedTemplate ? 'Shablonni tahrirlash' : 'Yangi shablon'}
        open={templateModalOpen}
        onCancel={() => {
          setTemplateModalOpen(false);
          templateForm.resetFields();
          setSelectedTemplate(null);
        }}
        footer={null}
        width={500}
      >
        <Form
          form={templateForm}
          layout="vertical"
          onFinish={handleSaveTemplate}
        >
          <Form.Item
            name="name"
            label="Shablon nomi"
            rules={[{ required: true, message: 'Nom kiriting' }]}
          >
            <Input placeholder="Masalan: Yangi aktsiya" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Kategoriya"
            rules={[{ required: true, message: 'Kategoriya tanlang' }]}
          >
            <Select
              placeholder="Kategoriya tanlang"
              options={[
                { value: 'PROMO', label: 'Aktsiya' },
                { value: 'NEWS', label: 'Yangilik' },
                { value: 'SYSTEM', label: 'Tizim' },
                { value: 'REMINDER', label: 'Eslatma' },
              ]}
            />
          </Form.Item>

          <Form.Item
            name="title"
            label="Sarlavha"
            rules={[{ required: true, message: 'Sarlavha kiriting' }]}
          >
            <Input placeholder="Bildirishnoma sarlavhasi" maxLength={100} />
          </Form.Item>

          <Form.Item
            name="body"
            label="Matn"
            rules={[{ required: true, message: 'Matn kiriting' }]}
          >
            <TextArea
              placeholder="Bildirishnoma matni"
              rows={4}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setTemplateModalOpen(false)}>Bekor qilish</Button>
              <Button type="primary" htmlType="submit">
                Saqlash
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
